import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Plus, Edit2, Trash2, X, Check, Loader2, 
  Calendar, User, Clock, Search
} from 'lucide-react';

const CATEGORIES = [
  'Hospital Setup', 'OT Planning', 'ICU Planning', 
  'Medical Equipment', 'Healthcare Industry', 'Pharmaceutical Distribution'
];

export default function ManageBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null); // null for new
  
  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');

  // Form Fields
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [author, setAuthor] = useState('Mr. Kalpesh Shah');
  const [readTime, setReadTime] = useState('5 mins read');
  const [tag, setTag] = useState('');
  const [desc, setDesc] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'blogs'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBlogs(list);
    } catch (err) {
      console.error("Error loading blogs:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load blog posts from database.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const openAddModal = () => {
    setCurrentBlog(null);
    setTitle('');
    setCategory(CATEGORIES[0]);
    setAuthor('Mr. Kalpesh Shah');
    setReadTime('5 mins read');
    setTag('');
    setDesc('');
    setContent('');
    setImage('');
    setModalOpen(true);
  };

  const openEditModal = (blog) => {
    setCurrentBlog(blog);
    setTitle(blog.title || '');
    setCategory(blog.category || CATEGORIES[0]);
    setAuthor(blog.author || 'Mr. Kalpesh Shah');
    setReadTime(blog.readTime || '5 mins read');
    setTag(blog.tag || '');
    setDesc(blog.desc || '');
    setContent(blog.content || '');
    setImage(blog.image || '');
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 819200) {
      Swal.fire({
        title: 'Image Too Large',
        text: 'The uploaded image exceeds the 800 KB limit. Please upload a smaller image.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.onerror = (err) => {
      console.error('Image reading error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to process image file.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !desc || !image) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Title, Description, and Cover Image are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Check for duplicate title
    const isDuplicate = blogs.some(b => 
      b.title.trim().toLowerCase() === title.trim().toLowerCase() && 
      (!currentBlog || b.id !== currentBlog.id)
    );
    if (isDuplicate) {
      Swal.fire({
        title: 'Duplicate Title',
        text: 'A blog post with this title already exists.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const blogData = {
      title: title.trim(),
      category,
      author: author.trim() || 'Mr. Kalpesh Shah',
      readTime: readTime.trim() || '5 mins read',
      tag: tag.trim(),
      desc: desc.trim(),
      content: content.trim(),
      image: image || '',
      date: currentBlog ? (currentBlog.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })) : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentBlog) {
        const docRef = doc(db, 'blogs', currentBlog.id);
        await updateDoc(docRef, blogData);
        Swal.fire({
          title: 'Success',
          text: 'Blog post successfully updated!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        blogData.createdAt = new Date();
        await addDoc(collection(db, 'blogs'), blogData);
        Swal.fire({
          title: 'Success',
          text: 'Blog post successfully created!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }
      
      await fetchBlogs();
      setTimeout(() => {
        setModalOpen(false);
      }, 1000);
    } catch (err) {
      console.error("Error saving blog post:", err);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving the blog post.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'blogs', id));
      setBlogs(blogs.filter(b => b.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Blog post successfully deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting blog post:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete blog post.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter(b => 
    b.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    b.desc.toLowerCase().includes(searchTerm.toLowerCase()) || 
    b.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Manage Blogs</h1>
          <p className="text-xs text-gray-500">Publish or revise expert industry guidelines, compliance standards, and setup planning insights.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="btn-primary py-2.5 px-5 text-sm shrink-0 self-start sm:self-center"
        >
          <Plus size={16} />
          Publish Article
        </button>
      </div>



      {/* Search Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-brand-silver/50 shadow-sm flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search blogs by title, keywords..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
          />
          <Search size={14} className="absolute left-3.5 top-3.5 text-gray-400" />
        </div>
      </div>

      {/* Blog List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
          <Loader2 className="animate-spin text-brand-blue" size={36} />
          <p className="text-xs font-bold text-gray-500">Loading published insights...</p>
        </div>
      ) : filteredBlogs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
          <BookOpen size={48} className="mx-auto text-gray-300" />
          <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Articles Found</h3>
          <p className="text-xs font-light text-gray-500">No blog posts match your filter. Click "Publish Article" to start broadcasting knowledge articles.</p>
          <button onClick={openAddModal} className="btn-primary py-2 px-4 text-xs mx-auto">Publish Article</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBlogs.map((blog) => (
            <motion.article
              key={blog.id}
              layout
              className="bg-white p-6 rounded-2xl border border-brand-silver/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-all group relative overflow-hidden"
            >
              <div className="space-y-4">
                {blog.image && (
                  <div className="w-full h-40 rounded-xl overflow-hidden relative shadow-sm border border-brand-silver/40">
                    <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
                  </div>
                )}
                {/* Header info */}
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="bg-brand-blue/10 text-brand-blue text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded">
                        {blog.category}
                      </span>
                    </div>
                    <h3 className="text-base font-extrabold font-montserrat text-brand-navy leading-snug group-hover:text-brand-blue transition-colors">
                      {blog.title}
                    </h3>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                    <button 
                      onClick={() => openEditModal(blog)}
                      className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-brand-blue hover:bg-brand-blue/5 transition-colors"
                      title="Edit Post"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button 
                      onClick={() => handleDelete(blog.id)}
                      className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete Post"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Snippet Description */}
                <p className="text-xs text-gray-500 leading-relaxed font-light line-clamp-2">
                  {blog.desc}
                </p>

                {/* Footer specs */}
                <div className="flex flex-wrap items-center gap-4 text-[10px] text-gray-400 font-poppins pt-2 border-t border-brand-silver/50">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {blog.date}</span>
                  <span className="flex items-center gap-1"><User size={12} /> {blog.author}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {blog.readTime}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      )}

      {/* CRUD Overlay Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentBlog ? 'Edit Blog Publication' : 'Publish Blog Article'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide configurations for knowledge desk articles.</p>
                </div>
                <button 
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-6 space-y-5">

                {/* Title */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Article Title</label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. Operation Theatre Design Standards in Maharashtra"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                  />
                </div>

                {/* Category Dropdown */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Category Segment</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Author */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Author Name</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Mr. Kalpesh Shah"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Read Time */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Estimated Read Time</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. 6 mins read"
                      value={readTime}
                      onChange={(e) => setReadTime(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Brief Snippet Description */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Brief Summary</label>
                  <textarea 
                    rows={2}
                    required
                    placeholder="Write brief summary..."
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Blog Image Upload */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Blog Article Cover Image * (Up to 800 KB)</label>
                  {!image ? (
                    <div className="relative border-2 border-dashed border-brand-silver hover:border-brand-blue rounded-xl p-6 flex flex-col items-center justify-center bg-brand-light/35 transition-colors cursor-pointer">
                      <input 
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="text-center space-y-1.5">
                        <Plus className="mx-auto text-gray-400" size={24} />
                        <p className="text-xs text-gray-500 font-poppins font-semibold text-brand-blue">Click to upload cover image</p>
                        <p className="text-[10px] text-gray-400 font-poppins">PNG, JPG or WEBP (Max 800 KB)</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 rounded-xl border border-brand-blue/20 bg-brand-blue/5 shadow-inner">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-brand-silver shrink-0 bg-white">
                          <img src={image} alt="Blog preview" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-brand-navy font-poppins">Image uploaded successfully</p>
                          <p className="text-[10px] text-gray-500 font-poppins">Size is under 800 KB limit</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold font-poppins transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>



                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={saving}
                    onClick={() => setModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Publishing article...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentBlog ? 'Save Changes' : 'Publish Article'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
